import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom/client';
import ClariMeApp from './App.jsx';
import LandingPage from './components/LandingPage.jsx';
import SharedSummaryPage from './components/SharedSummaryPage.jsx';
import ProviderInfoPage from './components/ProviderInfoPage.jsx';
import PrivacyPage from './components/PrivacyPage.jsx';
import GetAppPage from './components/GetAppPage.jsx';
import TermsPage from './components/TermsPage.jsx';
import AuthFlow from './components/AuthFlow.jsx';
import PreVisitOnboarding, { hasCompletedOnboarding } from './components/PreVisitOnboarding.jsx';
import { App as CapacitorApp } from '@capacitor/app';
import { useHashRouter } from './hooks/useHashRouter.js';
import { useAuth } from './hooks/useAuth.js';
import { isBiometricEnrolled, authenticateWithBiometric, removeBiometricCredentials } from './services/biometricService.js';
import { isTokenExpired } from './services/tokenService.js';
import { getInviteInfo, acceptInvite } from './services/apiService.js';
import * as Sentry from '@sentry/react';
import './index.css';

// Sentry error monitoring — graceful no-op when VITE_SENTRY_DSN is unset.
// No Session Replay integration on purpose: it would record the screen, which
// shows visit summaries (PHI). We capture errors only, with no PII attached.
if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    tracesSampleRate: 0.1,
    sendDefaultPii: false,
    // Browser-extension noise filter: extensions inject content scripts into
    // our pages, and their unhandled errors surface in our window and get
    // reported here even though ClariMe never calls these APIs. Patterns from
    // Sentry's community-maintained inbound-filter list.
    ignoreErrors: [
      // WebExtension messaging APIs (ad blockers, password managers, etc.)
      /runtime\.sendMessage/i,
      /extension context invalidated/i,
      /Receiving end does not exist/i,
      /The message port closed before a response was received/i,
      // Extension resource loading
      /chrome-extension/i,
      /moz-extension/i,
      /safari-web-extension/i,
      // Common non-actionable browser noise
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications.',
    ],
    denyUrls: [
      // Errors whose stack frames originate inside an extension bundle
      /^chrome-extension:\/\//i,
      /^moz-extension:\/\//i,
      /^safari-web-extension:\/\//i,
      /^safari-extension:\/\//i,
    ],
  });
}

/**
 * Root component with routing + auth gating
 *
 * Routes:
 * - (default)            -> Landing page (public marketing)
 * - #/join               -> Auth flow (sign up / sign in)
 * - #/setup              -> Pre-visit onboarding (first-time users)
 * - #/app                -> Main app (requires auth)
 * - #/share/:shareId     -> Shared summary page (public, no auth)
 * - #/get                -> Smart app-download link (public, QR target)
 */
// Universal Links: extract an in-app hash route from an incoming
// https://clarime.health/... URL. Only /share/<token> is app-linked today
// (see public/.well-known/apple-app-site-association). Returns null for
// anything unrecognized so we never navigate on a malformed URL.
function hashRouteFromUniversalLink(urlString) {
  try {
    const url = new URL(urlString);
    const shareMatch = url.pathname.match(/^\/share\/([a-zA-Z0-9_-]+)\/?$/);
    if (shareMatch) return `/share/${shareMatch[1]}`;
  } catch {
    // Not a parseable URL — ignore
  }
  return null;
}

function Root() {
  const route = useHashRouter();
  const auth = useAuth();
  const [biometricChecked, setBiometricChecked] = useState(false);
  const [biometricInProgress, setBiometricInProgress] = useState(false);
  const [inviteContext, setInviteContext] = useState(null);

  // One-tap invite links (#/join/<token> from invitation emails).
  // Stash the token so it survives the whole sign-in flow, and load the
  // invite's context (group name, inviter, invited email) so AuthFlow can
  // greet the invitee and pre-fill their email.
  useEffect(() => {
    if (route.page !== 'join' || !route.inviteToken) return;
    sessionStorage.setItem('clarime_invite_token', route.inviteToken);
    getInviteInfo(route.inviteToken)
      .then((data) => setInviteContext(data.invite || null))
      .catch(() => setInviteContext(null)); // bad/expired token — plain sign-up still works
  }, [route.page, route.inviteToken]);

  // Once signed in (any path: fresh signup, OTP, social, Face ID), redeem the
  // pending invite token. Whoever holds the link joins the group, even if
  // they signed up with a different email than the one invited. The token is
  // cleared up front so a failing accept can't loop; the email-match
  // auto-link on the backend remains the fallback.
  useEffect(() => {
    if (!auth.isSignedIn) return;
    const token = sessionStorage.getItem('clarime_invite_token');
    if (!token) return;
    sessionStorage.removeItem('clarime_invite_token');
    acceptInvite(token)
      .then((res) => console.log(`[ClariMe] Joined ${res.groupName} via invite link`))
      .catch((err) => console.warn('[ClariMe] Invite accept failed:', err?.message));
  }, [auth.isSignedIn]);

  // Universal Links (native only). Warm launches fire appUrlOpen; cold
  // launches deliver the URL via getLaunchUrl. Both route to the hash
  // router, which renders SharedSummaryPage before any auth gating.
  useEffect(() => {
    if (!window.Capacitor?.isNativePlatform?.()) return;

    CapacitorApp.getLaunchUrl().then((launch) => {
      const hashRoute = launch?.url && hashRouteFromUniversalLink(launch.url);
      if (hashRoute) window.location.hash = hashRoute;
    }).catch(() => {});

    const listener = CapacitorApp.addListener('appUrlOpen', ({ url }) => {
      const hashRoute = hashRouteFromUniversalLink(url);
      if (hashRoute) window.location.hash = hashRoute;
    });
    return () => { listener.then((l) => l.remove()).catch(() => {}); };
  }, []);

  // Remember whether the user was ALREADY signed in when Root first mounted.
  // Used to decide whether to auto-redirect away from /join. If the user wasn't
  // signed in at mount, we assume the /join visit is an active AuthFlow — let
  // it finish its own steps (setPassword, success) even if auth.isSignedIn
  // flips to true mid-flow. Otherwise AuthFlow unmounts the moment it signs in
  // and screens like SetPasswordStep never render.
  const wasInitiallySignedIn = useRef(auth.isSignedIn);

  // When the user signs out, reset the ref so the NEXT sign-in flow can run
  // AuthFlow to completion (same reasoning as the initial-mount check).
  useEffect(() => {
    if (!auth.isSignedIn) {
      wasInitiallySignedIn.current = false;
    }
  }, [auth.isSignedIn]);

  // On native app launch: if biometrics enrolled + not signed in, prompt Face ID
  useEffect(() => {
    const isNative = window.Capacitor?.isNativePlatform?.();
    if (!isNative || auth.isSignedIn || biometricChecked) {
      setBiometricChecked(true);
      return;
    }

    if (!isBiometricEnrolled()) {
      setBiometricChecked(true);
      return;
    }

    // Returning user with biometrics — trigger Face ID
    setBiometricInProgress(true);
    // Mark that main.jsx has already attempted biometric this session.
    // AuthFlow's init() consumes this flag so it doesn't re-fire the Face ID
    // prompt immediately after a cancel here — avoids a double-prompt.
    sessionStorage.setItem('clarime_initial_biometric_attempted', '1');
    authenticateWithBiometric().then(async (result) => {
      if (result.success && result.token && isTokenExpired(result.token)) {
        // The Keychain JWT is past its server-side expiry. Signing in with
        // it would render the whole app but every API call would 401 —
        // including the visit upload AFTER a patient records (build-32 bug).
        // Route to email sign-in with a notice instead. Enrollment is kept:
        // completeSignIn rewrites the Keychain with the fresh token.
        sessionStorage.setItem('clarime_session_expired', '1');
      } else if (result.success && result.token) {
        // Pass the stored JWT so auth_token is restored for API calls
        auth.signIn({
          email: result.email,
          phone: result.phone,
          firstName: result.firstName,
          idToken: result.token,
        });
        window.location.hash = hasCompletedOnboarding() ? '/app' : '/setup';
      } else if (result.success && !result.token) {
        // Legacy enrollment before the JWT-storage fix. Clear stale Keychain
        // credentials so the user goes through normal email auth and re-enrolls.
        await removeBiometricCredentials();
      }
      // If cancelled/failed, fall through to normal auth flow
      setBiometricInProgress(false);
      setBiometricChecked(true);
    });
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  // Show nothing while Face ID prompt is active
  if (biometricInProgress) {
    return (
      <div className="fixed inset-0 bg-white flex items-center justify-center" style={{ height: '100dvh' }}>
        <div className="text-center">
          <div className="w-12 h-12 border-3 border-teal-500/20 border-t-teal-400 rounded-full animate-spin mx-auto mb-3" />
        </div>
      </div>
    );
  }

  // Public shared summary page - no auth required
  if (route.page === 'share') {
    return <SharedSummaryPage shareId={route.shareId} />;
  }

  // Provider information page - public, no auth required
  if (route.page === 'providers') {
    return <ProviderInfoPage />;
  }

  // Smart app-download page (clinic card QR target) - public, no auth required
  if (route.page === 'get') {
    return <GetAppPage />;
  }

  // Legal pages - public, no auth required
  if (route.page === 'privacy') {
    return <PrivacyPage />;
  }
  if (route.page === 'terms') {
    return <TermsPage />;
  }

  // Auth flow - sign up / sign in
  if (route.page === 'join') {
    // Store pending group join if present (for post-signup redirect)
    if (route.groupId) {
      sessionStorage.setItem('clarime_pending_group', route.groupId);
    }
    // Only auto-redirect if the user was ALREADY signed in when Root mounted
    // (e.g. a deep link to /join for someone who's already authenticated).
    // If they signed in during an active AuthFlow, let AuthFlow finish — it
    // will navigate itself via SuccessStep.navigateTo().
    if (auth.isSignedIn && wasInitiallySignedIn.current) {
      const pendingGroup = sessionStorage.getItem('clarime_pending_group');
      if (pendingGroup) {
        sessionStorage.removeItem('clarime_pending_group');
        window.location.hash = '/app';
        return null;
      }
      window.location.hash = hasCompletedOnboarding() ? '/app' : '/setup';
      return null;
    }
    return <AuthFlow auth={auth} inviteContext={inviteContext} />;
  }

  // Pre-visit onboarding (first-time setup after auth)
  if (route.page === 'setup') {
    if (!auth.isSignedIn) {
      window.location.hash = '/join';
      return null;
    }
    // If already completed onboarding, skip to app
    if (hasCompletedOnboarding()) {
      window.location.hash = '/app';
      return null;
    }
    return <PreVisitOnboarding auth={auth} />;
  }

  // Main app - requires auth
  if (route.page === 'app') {
    if (!auth.isSignedIn) {
      // Redirect to auth flow
      window.location.hash = '/join';
      return null;
    }
    return <ClariMeApp auth={auth} />;
  }

  // Default route: native app skips landing page, web shows it
  const isNative = window.Capacitor?.isNativePlatform?.();
  if (isNative || auth.isSignedIn) {
    // In native app or already signed in — render directly (don't rely on hash redirect)
    if (auth.isSignedIn) {
      if (hasCompletedOnboarding()) {
        window.location.hash = '/app';
        return <ClariMeApp auth={auth} />;
      } else {
        window.location.hash = '/setup';
        return <PreVisitOnboarding auth={auth} />;
      }
    } else {
      window.location.hash = '/join';
      return <AuthFlow auth={auth} />;
    }
  }

  // Web visitors see the marketing landing page
  return <LandingPage />;
}

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <Root />
  </React.StrictMode>
);
